import React, { useCallback, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import type { VerticalNavItemsType } from 'src/@core/layouts/types'
import type { BhyveHorizontalSubnavV2Props } from './BhyveHorizontalSubnavV2'
import { BhyveAppFooter } from './BhyveAppFooter'
import { BhyveAppHeader } from './BhyveAppHeader'
import { BhyveMobilePrimaryBar } from './BhyveMobilePrimaryBar'
import { BhyveNavChromeThemeProvider } from './BhyveNavChromeThemeProvider'
import { BhyveNavRailV2, BHYVE_NAV_RAIL_WIDTH_PX } from './BhyveNavRailV2'
import { useBhyvePrimaryContextTitle } from './useBhyvePrimaryContextTitle'
import { FeatureExperiencePreferencesLauncher } from '@/src/components/featureExperience/FeatureExperiencePreferencesLauncher'

export type BhyveNavigationShellV2Props = BhyveHorizontalSubnavV2Props & {
  navItems: VerticalNavItemsType
  children: React.ReactNode
}

export const BhyveNavigationShellV2: React.FC<BhyveNavigationShellV2Props> = ({
  navItems,
  children,
  ...subNavProps
}) => {
  const [secondaryBarElevated, setSecondaryBarElevated] = useState(false)
  const [primaryNavDrawerOpen, setPrimaryNavDrawerOpen] = useState(false)

  const theme = useTheme()
  const router = useRouter()

  /** Persistent rail only at `lg` and up (Materio overlay breakpoint). */
  const useCollapsedPrimaryNav = useMediaQuery(theme.breakpoints.down('lg'), { noSsr: true })

  /**
   * xs/sm: bottom bar + bottom-sheet menu (phones / narrow).
   * md–lg: header hamburger + breadcrumbs + top sheet (tablets).
   */
  const useMobileBottomPrimaryNav = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true })

  const primaryNavUsesBottomChrome = Boolean(useCollapsedPrimaryNav && useMobileBottomPrimaryNav)
  const primaryNavUsesTopMenuButton = Boolean(useCollapsedPrimaryNav && !useMobileBottomPrimaryNav)

  const closePrimaryNavDrawer = useCallback(() => setPrimaryNavDrawerOpen(false), [])
  const openPrimaryNavDrawer = useCallback(() => setPrimaryNavDrawerOpen(true), [])

  const mobilePrimaryTitle = useBhyvePrimaryContextTitle()

  useEffect(() => {
    setPrimaryNavDrawerOpen(false)
  }, [router.pathname])

  return (
    <Box
      data-navigation-experience='next'
      data-bhyve-app-shell
      sx={{
        position: 'relative',
        display: 'flex',
        width: '100%',
        height: '100vh',
        maxHeight: '100vh',
        overflow: 'hidden',
        bgcolor: '#f8f8f8'
      }}
    >
      {!useCollapsedPrimaryNav ? (
        <Box
          data-bhyve-app-sidebar
          sx={{
            flexShrink: 0,
            alignSelf: 'stretch',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            zIndex: theme.zIndex.drawer
          }}
        >
          <BhyveNavChromeThemeProvider fullHeight>
            <BhyveNavRailV2 navItems={navItems} />
          </BhyveNavChromeThemeProvider>
        </Box>
      ) : null}

      <Drawer
        anchor={useMobileBottomPrimaryNav ? 'bottom' : 'top'}
        open={useCollapsedPrimaryNav && primaryNavDrawerOpen}
        onClose={closePrimaryNavDrawer}
        ModalProps={{ keepMounted: false }}
        PaperProps={{
          sx: useMobileBottomPrimaryNav
            ? {
                /** Portrait: half-height sheet. Landscape: short vertical space — use most of the viewport so list can scroll and footer stays visible. */
                height: 'min(50vh, 100dvh)',
                maxHeight: 'min(50vh, 100dvh)',
                minHeight: 0,
                width: '100%',
                overflow: 'hidden',
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                display: 'flex',
                flexDirection: 'column',

                /** Safe-area padding lives on embedded rail footer (scroll area uses full sheet height). */
                pb: 0,
                '@media (orientation: landscape)': {
                  height: 'min(92dvh, 100dvh)',
                  maxHeight: 'min(92dvh, 100dvh)'
                }
              }
            : {
                height: 'min(50vh, 100dvh)',
                maxHeight: 'min(50vh, 100dvh)',
                minHeight: 0,
                width: '100%',
                overflow: 'hidden',
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
                display: 'flex',
                flexDirection: 'column',
                pt: 'env(safe-area-inset-top, 0px)',
                '@media (orientation: landscape)': {
                  height: 'min(92dvh, 100dvh)',
                  maxHeight: 'min(92dvh, 100dvh)'
                }
              }
        }}
      >
        <BhyveNavChromeThemeProvider fullHeight>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            <BhyveNavRailV2
              navItems={navItems}
              embedded
              onNavigate={closePrimaryNavDrawer}
              onCloseDrawer={closePrimaryNavDrawer}
            />
          </Box>
        </BhyveNavChromeThemeProvider>
      </Drawer>

      <Box
        data-bhyve-app-shell-column
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          height: '100%',
          maxHeight: '100%',
          display: 'grid',
          gridTemplateRows: primaryNavUsesBottomChrome ? 'auto minmax(0, 1fr) auto auto' : 'auto minmax(0, 1fr) auto',
          overflow: 'hidden'
        }}
      >
        <BhyveNavChromeThemeProvider>
          <BhyveAppHeader
            {...subNavProps}
            elevated={secondaryBarElevated}
            showPrimaryNavMenuButton={primaryNavUsesTopMenuButton}
            onOpenPrimaryNavMenu={openPrimaryNavDrawer}
            primaryNavMenuOpen={useCollapsedPrimaryNav && primaryNavDrawerOpen}
            suppressBreadcrumbs={primaryNavUsesBottomChrome}
          />
        </BhyveNavChromeThemeProvider>
        <Box
          data-bhyve-app-main
          component='main'
          onScroll={e => setSecondaryBarElevated(e.currentTarget.scrollTop > 8)}
          sx={{
            gridRow: 2,
            position: 'relative',
            zIndex: 0,
            minHeight: 0,
            minWidth: 0,
            width: '100%',
            maxWidth: '100%',
            overflow: 'auto',
            overscrollBehavior: 'contain',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {children}
        </Box>
        {primaryNavUsesBottomChrome ? (
          <Box sx={{ gridRow: 3, minWidth: 0 }}>
            <BhyveMobilePrimaryBar
              title={mobilePrimaryTitle}
              onOpenMenu={openPrimaryNavDrawer}
              menuOpen={primaryNavDrawerOpen}
            />
          </Box>
        ) : null}
        <BhyveAppFooter sx={{ gridRow: primaryNavUsesBottomChrome ? 4 : 3 }} />
      </Box>
      <FeatureExperiencePreferencesLauncher />
    </Box>
  )
}

export { BHYVE_NAV_RAIL_WIDTH_PX }
